import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackAddToCart } from '../utils/metaPixel';


const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    return {
      cartItems: [],
      cartItemCount: 0,
      cartTotal: 0,
      isCartOpen: false,
      addToCart: () => {},
      removeFromCart: () => {},
      updateQuantity: () => {},
      clearCart: () => {},
      openCart: () => {},
      closeCart: () => {}
    };
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  // Callback ref: checkout page can register a function to clear coupon when bundle is added
  const [onBundleAdded, setOnBundleAdded] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cartItems');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        localStorage.removeItem('cartItems');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  // Add item to cart — variant-aware, bundle-exploding
  const addToCart = (product) => {
    const isBundle = !!(product?.is_bundle || product?.isBundle);

    // ── VARIANT SAFETY GUARD ────────────────────────────────────────────────
    // Dynamic validation: only require color/size if the product actually has those attributes
    if (!isBundle && product?.variants && product?.variants?.length > 0) {
      const requiresColor = product?.variants?.some(v => v?.color && String(v?.color)?.trim() !== '');
      const requiresSize = product?.variants?.some(v => v?.size && String(v?.size)?.trim() !== '');

      if (requiresColor && !product?.selectedColor) {
        console.warn("Blocked: Missing required color.");
        return;
      }
      if (requiresSize && !product?.selectedSize) {
        console.warn("Blocked: Missing required size.");
        return;
      }
    }

    // ── BUNDLE EXPLOSION ────────────────────────────────────────────────────
    // CRITICAL: When is_bundle === true, we MUST NOT add the parent bundle item.
    // Only the individual child items enter the cart array.
    if (isBundle) {
      const bundleItems = product?.bundle_items || product?.bundleItems || [];

      // BUNDLE SAFETY GUARD: dynamic validation per child item's actual variant attributes
      if (bundleItems && bundleItems?.length > 0) {
        const missingSelections = bundleItems?.some(item => {
          if (!item?.variants || item?.variants?.length === 0) return false;
          const reqColor = item?.variants?.some(v => v?.color && String(v?.color)?.trim() !== '');
          const reqSize = item?.variants?.some(v => v?.size && String(v?.size)?.trim() !== '');
          return (reqColor && !item?.selectedColor) || (reqSize && !item?.selectedSize);
        });
        if (missingSelections) {
          console.warn('Bundle child items missing required variant selections — blocked.');
          return;
        }
      }

      const bundleDiscount = parseFloat(product?.bundle_discount_percentage ?? product?.bundleDiscountPercentage ?? 0);
      const bundleInstanceId = crypto.randomUUID();

      // ACTION 1 FIX: Use user-selected color/size from the PARENT product object,
      // not from childItem (child items don't carry the user's selection).
      const selectedColor = product?.selectedColor;
      const selectedSize = product?.selectedSize;

      const explodedItems = bundleItems?.map((childItem) => {
        const originalPrice = parseFloat(childItem?.price) || 0;
        const discountedPrice = bundleDiscount > 0
          ? parseFloat((originalPrice * (1 - bundleDiscount / 100))?.toFixed(2))
          : originalPrice;

        // Unique cart key per child item in this bundle instance
        const cartItemId = `bundle_${bundleInstanceId}_${childItem?.productId || childItem?.id}`;

        return {
          id: childItem?.productId || childItem?.id,
          cartItemId,
          title: childItem?.title,
          price: discountedPrice,
          original_price: originalPrice,
          bundle_instance_id: bundleInstanceId,
          bundle_discount_percentage: bundleDiscount,
          image: childItem?.image || product?.image || product?.images?.[0],
          imageAlt: childItem?.imageAlt || `${childItem?.title} product image`,
          category: product?.category,
          quantity: 1,
          inStock: true,
          stockQuantity: null,
          trackInventory: false,
          // ACTION 1 FIX: Inject user-selected variants from parent product scope
          selectedColor: childItem?.selectedColor || selectedColor || undefined,
          selectedSize: childItem?.selectedSize || selectedSize || undefined,
          variantId: childItem?.variantId || undefined,
          // NOT a bundle wrapper — these are individual child items
          is_bundle: false,
          bundle_items: undefined,
        };
      });

      // CRITICAL: Only add child items — parent bundle wrapper is NEVER pushed to cart
      setCartItems(prev => [...prev, ...explodedItems]);
      trackAddToCart(product);

      // ACTION 2: If a coupon is active, auto-clear it when a bundle is added
      if (typeof onBundleAdded === 'function') {
        onBundleAdded();
      }

      return; // ← Explicit return ensures parent bundle object is never processed further
    }

    // ── REGULAR / VARIANT PRODUCT ───────────────────────────────────────────
    const hasVariants = product?.variants && product?.variants?.length > 0;

    let effectiveStock = product?.stockQuantity;
    if (hasVariants && product?.variantId) {
      const variant = product?.variants?.find(v => v?.id === product?.variantId);
      effectiveStock = variant ? variant?.stock : 0;
    }

    if (!product?.inStock || (product?.trackInventory && effectiveStock === 0)) {
      console.warn('Cannot add out-of-stock product to cart');
      return;
    }

    const cartItemId = product?.selectedColor || product?.selectedSize
      ? `${product?.id}_${product?.selectedColor || ''}_${product?.selectedSize || ''}`
      : `${product?.id}`;

    // ACTION 3: Use the active promotional price as the cart price.
    // If compareAtPrice > price, it means a campaign is active: price = discounted active price.
    // original_price = compareAtPrice (the original before discount).
    const hasActiveCampaign = product?.compareAtPrice && Number(product?.compareAtPrice) > Number(product?.price);
    const cartPrice = parseFloat(product?.price);
    const cartOriginalPrice = hasActiveCampaign ? parseFloat(product?.compareAtPrice) : parseFloat(product?.price);

    setCartItems(prev => {
      const existingItemIndex = prev?.findIndex((item) => item?.cartItemId === cartItemId);

      if (existingItemIndex > -1) {
        const currentQuantity = prev?.[existingItemIndex]?.quantity;
        const newQuantity = currentQuantity + 1;

        if (product?.trackInventory && effectiveStock && newQuantity > effectiveStock) {
          console.warn(`Cannot exceed available stock: ${effectiveStock}`);
          return prev;
        }

        const updatedCart = [...prev];
        updatedCart[existingItemIndex] = { ...updatedCart?.[existingItemIndex], quantity: newQuantity };
        return updatedCart;
      } else {
        return [
          ...prev,
          {
            id: product?.id,
            cartItemId,
            title: product?.title,
            price: cartPrice,
            original_price: cartOriginalPrice,
            image: product?.image || product?.images?.[0],
            imageAlt: product?.imageAlt || `${product?.title} product image`,
            category: product?.category,
            quantity: 1,
            inStock: product?.inStock,
            stockQuantity: effectiveStock,
            trackInventory: product?.trackInventory,
            selectedColor: product?.selectedColor || undefined,
            selectedSize: product?.selectedSize || undefined,
            variantId: product?.variantId || undefined,
            is_bundle: false,
            bundle_items: undefined,
          }
        ];
      }
    });

    trackAddToCart(product);
  };

  // Remove item from cart — with BUNDLE INTEGRITY RULE
  const removeFromCart = (cartItemId) => {
    setCartItems(prev => {
      const removedItem = prev?.find((item) => (item?.cartItemId || item?.id) === cartItemId);

      // ── BUNDLE INTEGRITY RULE ─────────────────────────────────────────────
      // If the removed item belongs to a bundle instance, revert all sibling
      // items' prices back to their original_price and strip their bundle_instance_id.
      if (removedItem?.bundle_instance_id) {
        const instanceId = removedItem?.bundle_instance_id;
        return prev?.filter((item) => (item?.cartItemId || item?.id) !== cartItemId)?.map((item) => {
            if (item?.bundle_instance_id === instanceId) {
              return {
                ...item,
                price: item?.original_price ?? item?.price,
                bundle_instance_id: undefined,
                bundle_discount_percentage: undefined,
              };
            }
            return item;
          });
      }

      // Regular removal
      return prev?.filter((item) => (item?.cartItemId || item?.id) !== cartItemId);
    });
  };

  // Update item quantity — use cartItemId
  const updateQuantity = (cartItemId, amount) => {
    setCartItems(prev =>
      prev?.map((item) => {
        const key = item?.cartItemId || item?.id;
        if (key === cartItemId) {
          const newQuantity = item?.quantity + amount;
          if (newQuantity < 1) return item;
          if (item?.trackInventory && item?.stockQuantity && newQuantity > item?.stockQuantity) {
            console.warn(`Cannot exceed available stock: ${item?.stockQuantity}`);
            return item;
          }
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  // Clear all items from cart
  const clearCart = () => {
    setCartItems([]);
  };

  // Calculate cart total
  const cartTotal = cartItems?.reduce((sum, item) => {
    return sum + item?.price * item?.quantity;
  }, 0);

  // Get cart item count
  const cartItemCount = cartItems?.reduce((count, item) => count + item?.quantity, 0);

  // Open/close cart drawer
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const value = {
    cartItems,
    cartItemCount,
    cartTotal,
    isCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    openCart,
    closeCart,
    // Expose setter so checkout page can register a bundle-added callback
    registerBundleAddedCallback: (fn) => setOnBundleAdded(() => fn),
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;